import { useEffect, useState } from 'react'
import { Alert, CircularProgress } from '@mui/material'
import { documentUrl } from '@/hooks/useInbox'
import {
  ENCLOSURE_REASON_LABELS,
  FLIXPECTOR_STATUS_LABELS,
  INBOX_STATE_LABELS,
  confidenceMeta,
  enclosureConfidenceMeta,
  fileMeta,
  isPdf,
  isSpreadsheet,
  ratingDot,
  scoreMeta,
  type InboxItem,
  type InboxItemKind,
  type SpreadsheetPreview,
} from '@/types/inbox'
import {
  Button,
  Dot,
  DrawerHeader,
  HRule,
  Icon,
  Pill,
  SectionLabel,
  Select,
  SideDrawer,
  Spacer,
  WarningBlock,
} from '@/components/ui'
import { formatAmount, formatDate, formatDateTime } from '@/utils/inboxFormat'
import { useAssignableUsers } from '@/hooks/useUsers'
import { useT } from '@/i18n/useT'
import { EnclosureList } from './EnclosureList'

/**
 * Renders the original document inside the drawer's framed box: PDFs inline,
 * spreadsheets as the server-rendered table, attachment-less mail as its body
 * text. This is the part the design mockup only sketched as a placeholder.
 */
function OriginalPreview({
  item,
  loadPreview,
}: {
  item: InboxItem
  loadPreview: (id: string) => Promise<SpreadsheetPreview>
}) {
  const { t } = useT()
  const [preview, setPreview] = useState<SpreadsheetPreview | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)

  useEffect(() => {
    setPreview(null)
    setPreviewError(null)
    if (!item.documentId || !isSpreadsheet(item)) return

    loadPreview(item.id)
      .then(setPreview)
      .catch((e) => setPreviewError(e instanceof Error ? e.message : t('Preview failed')))
  }, [item, loadPreview, t])

  const file = fileMeta(item)

  if (!item.documentId) {
    return (
      <div
        style={{
          border: '1px solid var(--line)',
          borderRadius: 10,
          background: 'var(--field)',
          padding: 14,
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 6 }}>
          {t('No attachment — content of the mail')}
        </div>
        <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          {item.bodyPreview || t('No text available.')}
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 10,
        background: 'var(--field)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          borderBottom: '1px solid var(--line2)',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            padding: '5px 8px',
            borderRadius: 5,
            color: file.fg,
            background: file.bg,
          }}
        >
          {file.tag}
        </span>
        <Spacer />
        <a
          className="fx-btn fx-btn--outline fx-btn--sm"
          href={documentUrl(item.documentId)}
          target="_blank"
          rel="noopener"
        >
          <Icon name="download" size={14} />
          {t('Download')}
        </a>
        <a
          className="fx-btn fx-btn--ghost fx-btn--sm"
          href={documentUrl(item.documentId, true)}
          target="_blank"
          rel="noopener"
        >
          <Icon name="external" size={14} />
          {t('In a new tab')}
        </a>
      </div>

      {isPdf(item) && (
        <iframe
          src={documentUrl(item.documentId, true)}
          title={item.filename ?? t('Document')}
          style={{ width: '100%', height: 420, border: 'none', display: 'block' }}
        />
      )}

      {isSpreadsheet(item) && (
        <div style={{ padding: 12 }}>
          {previewError && <Alert severity="warning">{previewError}</Alert>}
          {!preview && !previewError && <CircularProgress size={20} />}
          {preview && (
            <>
              <div style={{ fontSize: 11, color: 'var(--faint)', marginBottom: 6 }}>
                {t('Sheet')} „{preview.sheetName}"
                {preview.truncated &&
                  ` — ${t('first {n} of {total} rows')
                    .replace('{n}', String(preview.rows.length))
                    .replace('{total}', String(preview.totalRows))}`}
              </div>
              <div style={{ maxHeight: 360, overflow: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
                  <tbody>
                    {preview.rows.map((row, i) => (
                      <tr key={i} style={{ background: i === 0 ? 'var(--hover)' : undefined }}>
                        {row.map((cell, j) => (
                          <td
                            key={j}
                            style={{
                              whiteSpace: 'nowrap',
                              padding: '5px 8px',
                              borderBottom: '1px solid var(--line2)',
                              fontWeight: i === 0 ? 700 : 400,
                            }}
                          >
                            {cell == null ? '' : String(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {!isPdf(item) && !isSpreadsheet(item) && (
        <div style={{ padding: 14, fontSize: 13, color: 'var(--muted)' }}>
          {t('No preview for this file type — please download it.')}
        </div>
      )}
    </div>
  )
}

export function InboxDetailDrawer({
  item,
  busy,
  error,
  onClose,
  loadPreview,
  onChangeKind,
  onDelete,
  onDismiss,
  onConfirmEnclosure,
  onOwnInvoice,
  onSend,
  onAccept,
  onSync,
  onSyncBack,
}: {
  item: InboxItem | null
  busy: boolean
  error: string | null
  onClose: () => void
  loadPreview: (id: string) => Promise<SpreadsheetPreview>
  onChangeKind: (kind: InboxItemKind) => void
  onDelete: () => void
  onDismiss: () => void
  /** Enclosure suggestion confirmed: leaves the queue, no invoice row */
  onConfirmEnclosure: () => void
  /** Suggestion rejected: becomes a normal invoice again */
  onOwnInvoice: () => void
  /** Never called without a Bearbeiter — the button stays disabled until one is picked */
  onSend: (assigneeUserId: string) => void
  onAccept: () => void
  onSync: () => void
  onSyncBack: () => void
}) {
  // Hooks stay above the early return: this component is always mounted and
  // merely renders nothing while no item is selected.
  const { users, error: usersError } = useAssignableUsers()
  const { t } = useT()
  const [assigneeUserId, setAssigneeUserId] = useState('')

  // Follow the opened item — pre-filling with whoever already owns it, so
  // re-opening a handed-over document does not look unassigned.
  useEffect(() => {
    setAssigneeUserId(item?.assigneeUserId ?? '')
  }, [item?.id, item?.assigneeUserId])

  if (!item) return null

  const conf = confidenceMeta(item.confidence)
  const score = scoreMeta(item)
  const isNew = item.state === 'new'
  const isReturned = item.state === 'returned_from_flixpector'
  // Both fresh mail and a reviewed return are waiting on the user. Only a
  // fresh item may still be re-typed or handed over; a return already has an
  // audit, so sending it again would open a second one.
  const canDecide = isNew || isReturned
  const invoiced = item.flixpectorTotalAmount
  const agreed = item.flixpectorFinalAmount
  const isEnclosure = item.kind === 'enclosure'
  const encl = item.enclosure ?? null
  const enclConf = enclosureConfidenceMeta(encl?.confidence ?? null)
  const saving = invoiced != null && agreed != null ? invoiced - agreed : null

  return (
    <SideDrawer
      open
      onClose={onClose}
      header={
        <DrawerHeader
          title={item.supplierName || item.subject || t('(no subject)')}
          subtitle={item.subject || t('(no subject)')}
          onClose={onClose}
          leading={<Dot color={ratingDot(item)} size={10} />}
        />
      }
      footer={
        canDecide ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Button variant="danger" onClick={onDelete} disabled={busy}>
              <Icon name="trash" size={15} />
              {t('Delete')}
            </Button>
            <Spacer />
            {item.kind === 'other' && (
              <Button
                variant="ghost"
                onClick={onDismiss}
                disabled={busy}
                title={t('Take off the list — stays stored as a record')}
              >
                {t('File without a document')}
              </Button>
            )}
            {isNew && isEnclosure && (
              <>
                <Button variant="outline" onClick={onOwnInvoice} disabled={busy}>
                  {t('Own invoice after all')}
                </Button>
                <Button variant="accent" onClick={onConfirmEnclosure} disabled={busy}>
                  {t('Confirm as enclosure')}
                </Button>
              </>
            )}
            {isNew && item.kind !== 'other' && !isEnclosure && (
              // No hand-off without a named owner — the review has to belong to
              // someone on this side too, not just in Flixpector.
              <Button
                variant="outline"
                onClick={() => onSend(assigneeUserId)}
                disabled={busy || !assigneeUserId}
                title={assigneeUserId ? undefined : t('Choose a handler first')}
              >
                {t('Send to Flixpector')}
              </Button>
            )}
            {item.kind === 'invoice' && (
              <Button variant="accent" onClick={onAccept} disabled={busy}>
                {t('Add to database')}
              </Button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: 'var(--muted)', flex: 1, minWidth: 160 }}>
              {isEnclosure && item.state === 'dismissed'
                ? t('Filed as enclosure')
                : t(INBOX_STATE_LABELS[item.state])}
              {item.flixpectorId && ` · ${t('Flixpector ID')} ${item.flixpectorId}`}
            </div>
            {item.state === 'sent_to_flixpector' && (
              <Button variant="outline" onClick={onSyncBack} disabled={busy}>
                <Icon name="refresh" size={15} strokeWidth={2.2} />
                {t('Fetch result and return to the inbox')}
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>
              {t('Close')}
            </Button>
          </div>
        )
      }
    >
      {error && <Alert severity="error">{error}</Alert>}
      {item.error && <Alert severity="warning">{item.error}</Alert>}

      <WarningBlock items={item.warnings ?? []} />

      {isEnclosure && (
        <div
          style={{
            border: '1px solid var(--kNeuBd)',
            borderRadius: 10,
            background: 'var(--kNeuBg)',
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--warn)', flex: 1 }}>
              {encl?.confidence === 'high'
                ? `${t('Confident suggestion')}: `
                : encl?.confidence === 'normal'
                  ? `${t('Suggestion')}: `
                  : ''}
              {t('Enclosure (expenses)')}
            </div>
            {enclConf.label && (
              <Pill fg={enclConf.fg} bg={enclConf.bg} border={enclConf.border}>
                {t(enclConf.label)}
              </Pill>
            )}
          </div>
          {encl && encl.reasons.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }}>
              {encl.reasons.map((r) => (
                <li key={r}>{t(ENCLOSURE_REASON_LABELS[r])}</li>
              ))}
            </ul>
          )}
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            {t(
              'Not booked as an invoice — the PDF stays available from the main invoice of this mail.',
            )}
          </div>
          <div>
            <SectionLabel>{t('Main invoice')}</SectionLabel>
            <div style={{ marginTop: 8 }}>
              {encl && encl.mainItems.length > 0 ? (
                <EnclosureList items={encl.mainItems} />
              ) : (
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                  {t('No main invoice in this mail')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {item.kind === 'invoice' && (item.enclosures?.length ?? 0) > 0 && (
        <div>
          <SectionLabel>{t('Enclosures in this mail')}</SectionLabel>
          <div style={{ marginTop: 8 }}>
            <EnclosureList items={item.enclosures ?? []} showState />
          </div>
        </div>
      )}

      <div>
        <SectionLabel>{t('Origin')}</SectionLabel>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '116px 1fr',
            gap: '6px 14px',
            marginTop: 10,
            fontSize: 13,
          }}
        >
          <div style={{ color: 'var(--muted)' }}>{t('From')}</div>
          <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{item.fromAddress || '–'}</div>
          <div style={{ color: 'var(--muted)' }}>{t('Original')}</div>
          <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{item.originalSender || '–'}</div>
          <div style={{ color: 'var(--muted)' }}>{t('Received')}</div>
          <div>{formatDateTime(item.receivedAt ?? item.createdAt)}</div>
        </div>
      </div>

      <HRule />

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SectionLabel style={{ flex: 1 }}>{t('Extracted data')}</SectionLabel>
          <Pill fg={conf.fg} border={conf.border} style={{ padding: '3px 9px' }}>
            {t(conf.label)}
          </Pill>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 10,
            marginTop: 10,
          }}
        >
          {[
            { label: t('Supplier'), value: item.supplierName || '–' },
            { label: t('Invoice number'), value: item.invoiceNumber || '–' },
            { label: t('Invoice date'), value: formatDate(item.invoiceDate) },
            { label: t('Gross amount'), value: formatAmount(item.totalGross, item.currency) },
          ].map((f) => (
            <div
              key={f.label}
              style={{
                background: 'var(--field)',
                border: '1px solid var(--line2)',
                borderRadius: 8,
                padding: '10px 12px',
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--faint)' }}>{f.label}</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 3, overflowWrap: 'anywhere' }}>
                {f.value}
              </div>
            </div>
          ))}
        </div>

        {isNew && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t('Document kind')}</div>
            <Select<InboxItemKind>
              value={item.kind}
              onChange={onChangeKind}
              ariaLabel={t('Document kind')}
              height={34}
              options={[
                { value: 'invoice', label: t('Invoice') },
                { value: 'service_description', label: t('Service record') },
                ...(item.documentId || item.kind === 'enclosure'
                  ? [{ value: 'enclosure' as const, label: t('Enclosure (expenses)') }]
                  : []),
                { value: 'other', label: t('Mail without a document') },
              ]}
            />
          </div>
        )}
      </div>

      {/* Bearbeiter — chosen before the hand-off and travels with the document
          to Flixpector, so the review has a named owner on both sides. */}
      {(isNew && item.kind !== 'other' && !isEnclosure) || item.assigneeUserId ? (
        <div>
          <SectionLabel>{t('Handler (Flixpector review)')}</SectionLabel>
          {isNew && item.kind !== 'other' && !isEnclosure ? (
            <>
              <div style={{ marginTop: 10 }}>
                <Select<string>
                  value={assigneeUserId}
                  onChange={setAssigneeUserId}
                  ariaLabel={t('Handler')}
                  height={34}
                  options={[
                    { value: '', label: t('Choose a handler …') },
                    ...users.map((u) => ({
                      value: u.id,
                      label: u.initials ? `${u.fullName} (${u.initials})` : u.fullName,
                    })),
                  ]}
                />
              </div>
              <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 6 }}>
                {usersError
                  ? usersError
                  : t(
                      'Who checks the law-firm invoice in Flixpector — not the approver (that comes from the PO). Required for the hand-over and shown there as initials on every line.',
                    )}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
              <Pill fg="var(--kSrvFg)" bg="var(--kSrvBg)" border="var(--kSrvBd)">
                {item.assigneeInitials ?? '—'}
              </Pill>
              <div style={{ fontSize: 13 }}>{item.assigneeName ?? t('Unknown')}</div>
            </div>
          )}
        </div>
      ) : null}

      {item.flixpectorId && (
        <div style={{ border: '1px solid var(--accentBorder)', borderRadius: 10, overflow: 'hidden' }}>
          <div
            style={{
              padding: '10px 14px',
              background: 'var(--accentSoft)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--accent)' }}>
              FLIXPECTOR
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', flex: 1, minWidth: 120 }}>
              {(() => {
                const label = FLIXPECTOR_STATUS_LABELS[item.flixpectorStatus ?? '']
                // An unmapped status is Flixpector's own raw value — show it as
                // it came, rather than guessing at a translation for it.
                return `${label ? t(label) : (item.flixpectorStatus ?? t('No result yet'))} · ${item.flixpectorId}`
              })()}
            </div>
            <span
              title={
                score.score != null
                  ? t(score.title).replace('{score}', score.score.toFixed(1))
                  : t(score.title)
              }
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '3px 9px',
                borderRadius: 999,
                color: '#0f172a',
                background: ratingDot(item),
                whiteSpace: 'nowrap',
              }}
            >
              {t('Risk')} {score.text}
            </span>
          </div>
          <div
            style={{
              padding: 14,
              display: 'flex',
              gap: 18,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
            }}
          >
            <div>
              <div style={{ fontSize: 11, color: 'var(--faint)' }}>{t('Invoiced')}</div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  textDecoration: saving != null && saving > 0 ? 'line-through' : 'none',
                  color: saving != null && saving > 0 ? 'var(--strike)' : 'var(--text)',
                }}
              >
                {formatAmount(invoiced, item.currency)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--faint)' }}>{t('Agreed')}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ok)' }}>
                {formatAmount(agreed, item.currency)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--faint)' }}>{t('Savings')}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ok)' }}>
                {saving != null ? formatAmount(saving, item.currency) : '–'}
              </div>
            </div>
            <Spacer />
            <Button variant="outline" size="sm" onClick={onSync} disabled={busy}>
              {t('Fetch result')}
            </Button>
          </div>
          {/* Who last acted in Flixpector's Auditor (its signed-in reviewer).
              We no longer write this field — the Bearbeiter now travels as the
              per-position owner — so there is nothing to compare it against. */}
          {item.flixpectorReviewer && (
            <div
              style={{
                padding: '0 14px 12px',
                fontSize: 12,
                color: 'var(--muted)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ color: 'var(--faint)' }}>{t('Reviewed in Flixpector by:')}</span>
              <strong style={{ color: 'var(--text)' }}>{item.flixpectorReviewer}</strong>
            </div>
          )}
          {item.flixpectorSyncedAt && (
            <div style={{ padding: '0 14px 12px', fontSize: 11, color: 'var(--faint)' }}>
              {t('Last fetched:')} {formatDateTime(item.flixpectorSyncedAt)}
            </div>
          )}
        </div>
      )}

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SectionLabel style={{ flex: 1 }}>{t('Original document')}</SectionLabel>
          {/* A filename without a stored document would promise a preview that
              cannot be shown, so the header follows what is actually there. */}
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            {item.documentId ? item.filename : t('No attachment')}
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <OriginalPreview item={item} loadPreview={loadPreview} />
        </div>
      </div>
    </SideDrawer>
  )
}
